/** @format */

import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { makeStyles, TableBody, TableFooter, TableHead } from '@material-ui/core';
import Order from '../../../interfaces/forms/Order';
import adescomStyle from '../../../assets/jss/adescom/adescomStyle';
import { FilterReducerFieldName } from '../../../modules/filter/FilterReducers';
import { FilterListReducerFieldName } from '../../../modules/filter/FilterListReducers';
import { getPrefixUploadFileStatus, PREFIX_UPLOAD_STATUS } from '../../../modules/prefixes/helpers/PrefixUploadHelper';
import GridContainer from '../../../components/Grid/GridContainer';
import PaperedTable from '../../../components/Table/PaperedTable';
import GridItem from '../../../components/Grid/GridItem';
import CustomTextButtom from '../../../components/Buttons/CustomTextButton';
import TableRow from '@material-ui/core/TableRow';
import FilterList from '../../../components/Filter/FilterList';
import TableCell from '@material-ui/core/TableCell';
import TableHeaderContentCell from '../../../components/Table/TableHeaderContentCell';
import TemplateButton from '../../../components/Buttons/TemplateButton';
import I18n from '../../../components/Translate/I18n';
import { DateHelperTs } from '../../../helpers/DateHelperTs';
import Pager from '../../../components/Adescom/Pager';
import Filter from '../../../interfaces/forms/Filter';
import { Report } from '@material-ui/icons';
import { ServiceListUploadInterface } from './ServiceListUploadInterface';
import { useServiceListUploadCollection } from './useServiceListUploadCollection';
import ServiceListUploadReportView from './ServiceListUploadReportView';

interface ServiceListUploadFileListInterface {
    reload: boolean;
    setReload: (reload: boolean) => void;
}
const ServiceListUploadList: React.FC<ServiceListUploadFileListInterface> = (props) => {
    const { reload, setReload } = props;
    const [filters, setFilters] = useState<Array<Filter>>([]);
    const [orders, setOrders] = useState<Array<Order>>([]);
    const [selectedServiceListUploadFile, setSelectedServiceListUploadFile] = useState<ServiceListUploadInterface | null>(null);

    const { serviceListUploadCollection, state, refreshCollectionCallback, getCollectionCallback } = useServiceListUploadCollection(filters, orders);

    const dispatch = useDispatch();
    const useStyle = makeStyles(adescomStyle);
    const classes = useStyle();

    const tableStickyHeader = { root: classes.tableStickyHeader };
    const tableStickyCommand = { root: classes.tableStickyCommand };
    const tableCellRoot = { root: classes.tableCellRootBordered };

    const updateOrderList = (orders: Array<Order>): void => setOrders(orders);
    const updateFilterList = (filters: Array<Filter>): void => setFilters(filters);

    const clearFilter = (): void => {
        dispatch({
            type: FilterReducerFieldName.HEADER_FILTER_MANAGE,
            clearAll: true,
        });
        dispatch({
            type: FilterListReducerFieldName.HEADER_FILTER_MANAGE,
        });
    };

    const setDefaultFilter = (callback: any): void => {
        setOrders([]);
        setFilters([]);
        callback();
    };

    const paginationCallbackFunction = (uri: string): void => {
        getCollectionCallback(uri);
    };

    const viewReport = (serviceListUploadInterface: ServiceListUploadInterface): void => {
        if ([PREFIX_UPLOAD_STATUS.STATUS_IMPORT_AWAIT, PREFIX_UPLOAD_STATUS.STATUS_IMPORT_PROCESS].includes(serviceListUploadInterface.status)) {
            return;
        }

        setSelectedServiceListUploadFile(serviceListUploadInterface);
    };

    const closeReportView = (): void => {
        setSelectedServiceListUploadFile(null);
    };

    useEffect(() => {
        if (reload) {
            refreshCollectionCallback();
        }
    }, [reload, refreshCollectionCallback]);

    return (
        <GridContainer spacing={2}>
            <GridItem xs={12} md={12} lg={12}>
                <PaperedTable
                    beforeTableContent={
                        <GridContainer>
                            <GridItem xs={12} className={classes.beforeTableContent}>
                                <CustomTextButtom
                                    callback={refreshCollectionCallback}
                                    callbackParam={{}}
                                    color="primary"
                                    label="general:common.search"
                                    size="lg"
                                />
                                {(filters.length > 0 || orders.length > 0) && (
                                    <CustomTextButtom
                                        callback={(): void => setDefaultFilter(clearFilter)}
                                        callbackParam={{}}
                                        color="primary"
                                        label="general:common.clear"
                                        size="lg"
                                    />
                                )}
                                <FilterList />
                            </GridItem>
                        </GridContainer>
                    }
                >
                    <TableHead>
                        <TableRow>
                            <TableCell classes={tableStickyCommand} padding="none" align="left">
                                <TableHeaderContentCell textAlign="left" title="general:common.commands" onlyFilter={true} />
                            </TableCell>
                            <TableCell classes={tableStickyHeader} align="left">
                                <TableHeaderContentCell
                                    textAlign="left"
                                    title="prefixes:upload.file_name"
                                    updateOrderList={updateOrderList}
                                    updateFilterList={updateFilterList}
                                    fieldName="fileName"
                                    showSort={true}
                                    defaultDirection="desc"
                                    searchFieldType="string"
                                    filters={filters}
                                    sort={orders}
                                />
                            </TableCell>
                            <TableCell classes={tableStickyHeader} align="left">
                                <TableHeaderContentCell
                                    textAlign="left"
                                    title="prefixes:upload.status"
                                    updateOrderList={updateOrderList}
                                    updateFilterList={updateFilterList}
                                    fieldName="status"
                                    showSort={true}
                                    defaultDirection="desc"
                                    searchFieldType="select"
                                    selectComponent="headerCheckBoxSimpleList"
                                    elementList={[
                                        { name: 'prefixes:upload.status_uploaded', value: PREFIX_UPLOAD_STATUS.STATUS_IMPORT_AWAIT },
                                        { name: 'prefixes:upload.status_import_process', value: PREFIX_UPLOAD_STATUS.STATUS_IMPORT_PROCESS },
                                        { name: 'prefixes:upload.status_imported', value: PREFIX_UPLOAD_STATUS.STATUS_IMPORT_FINISH },
                                        { name: 'prefixes:upload.status_error', value: PREFIX_UPLOAD_STATUS.STATUS_ERROR },
                                    ]}
                                    filters={filters}
                                    sort={orders}
                                />
                            </TableCell>
                            <TableCell classes={tableStickyHeader} align="left">
                                <TableHeaderContentCell
                                    textAlign="left"
                                    title="prefixes:upload.import_by_date"
                                    updateOrderList={updateOrderList}
                                    updateFilterList={updateFilterList}
                                    fieldName="importByDate"
                                    defaultDirection="desc"
                                    filters={filters}
                                    sort={orders}
                                    showSort={true}
                                    onlyFilter={false}
                                    searchFieldType="date"
                                    dateFormat="RRRR-MM-DD"
                                    beforeSetToMax={true}
                                    afterSetToMin={true}
                                    dateFromTo={true}
                                    breakLine={true}
                                    timeFormat={false}
                                />
                            </TableCell>
                            <TableCell classes={tableStickyHeader} align="left">
                                <TableHeaderContentCell
                                    textAlign="left"
                                    title="prefixes:upload.import_date"
                                    updateOrderList={updateOrderList}
                                    updateFilterList={updateFilterList}
                                    fieldName="importDate"
                                    defaultDirection="desc"
                                    filters={filters}
                                    sort={orders}
                                    showSort={true}
                                    onlyFilter={false}
                                    searchFieldType="date"
                                    dateFormat="RRRR-MM-DD"
                                    beforeSetToMax={true}
                                    afterSetToMin={true}
                                    dateFromTo={true}
                                    breakLine={true}
                                    timeFormat={false}
                                />
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {serviceListUploadCollection &&
                            serviceListUploadCollection['hydra:member'].map((serviceListUpload) => (
                                <TableRow key={serviceListUpload.id}>
                                    <TableCell classes={tableCellRoot} padding="none" align="left">
                                        <TemplateButton
                                            icon={Report}
                                            callback={viewReport}
                                            callbackParam={serviceListUpload}
                                            tooltipTitle={
                                                [PREFIX_UPLOAD_STATUS.STATUS_IMPORT_AWAIT, PREFIX_UPLOAD_STATUS.STATUS_IMPORT_PROCESS].includes(
                                                    serviceListUpload.status
                                                )
                                                    ? 'prefixes:upload.report_view_disabled'
                                                    : 'prefixes:upload.report_view_enabled'
                                            }
                                        />
                                    </TableCell>
                                    <TableCell classes={tableCellRoot} padding="none" align="left">
                                        {serviceListUpload.fileName}
                                    </TableCell>
                                    <TableCell classes={tableCellRoot} padding="none" align="left">
                                        <I18n trans={getPrefixUploadFileStatus(serviceListUpload.status)} />
                                    </TableCell>
                                    <TableCell classes={tableCellRoot} padding="none" align="left">
                                        {DateHelperTs.format(serviceListUpload.importByDate)}
                                    </TableCell>
                                    <TableCell classes={tableCellRoot} padding="none" align="left">
                                        {serviceListUpload.importDate && DateHelperTs.format(serviceListUpload.importDate)}
                                    </TableCell>
                                </TableRow>
                            ))}
                    </TableBody>
                    <TableFooter>
                        <TableRow>
                            <TableCell classes={tableCellRoot} colSpan={4}>
                                {serviceListUploadCollection && serviceListUploadCollection['hydra:view'] && (
                                    <Pager callback={paginationCallbackFunction} views={serviceListUploadCollection['hydra:view']} />
                                )}
                            </TableCell>
                        </TableRow>
                    </TableFooter>
                </PaperedTable>
            </GridItem>
            <GridItem xs={12}>
                <ServiceListUploadReportView serviceListUpload={selectedServiceListUploadFile} closeReportView={closeReportView} />
            </GridItem>
        </GridContainer>
    );
};

export default ServiceListUploadList;
